const SUPABASE_URL = 'https://kmfjbtmmtuwxoxrliauq.supabase.co'
const SUPABASE_PUBLISHABLE_KEY =
  'sb_publishable_jeHgpiXz0PKKx1YCJgjoNQ_BetEBaSf'
const SESSION_KEY = 'jumpland_supabase_session'

interface SupabaseErrorBody {
  message?: string
  error?: string
  error_description?: string
  hint?: string
}

interface AuthUser {
  id: string
  email?: string
  app_metadata?: {
    role?: string
  }
}

interface AuthSessionResponse {
  access_token: string
  refresh_token: string
  expires_in: number
  expires_at?: number
  user: AuthUser
}

export interface SupabaseSession {
  accessToken: string
  refreshToken: string
  expiresAt: number
  user: AuthUser
}

export class SupabaseRequestError extends Error {
  status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'SupabaseRequestError'
    this.status = status
  }
}

const parseSession = (data: AuthSessionResponse): SupabaseSession => ({
  accessToken: data.access_token,
  refreshToken: data.refresh_token,
  expiresAt:
    data.expires_at ||
    Math.floor(Date.now() / 1000) + Math.max(data.expires_in, 0),
  user: data.user,
})

const persistSession = (session: SupabaseSession | null) => {
  if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session))
  else localStorage.removeItem(SESSION_KEY)
}

export const getStoredSession = (): SupabaseSession | null => {
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const session = JSON.parse(raw) as SupabaseSession
    if (!session.accessToken || !session.refreshToken || !session.expiresAt)
      return null
    return session
  } catch {
    persistSession(null)
    return null
  }
}

const getErrorMessage = async (response: Response): Promise<string> => {
  const bodyText = await response.text()
  try {
    const body = JSON.parse(bodyText) as SupabaseErrorBody
    return [body.message || body.error || body.error_description, body.hint]
      .filter(Boolean)
      .join(' ')
  } catch {
    return bodyText
  }
}

const authFetch = async (
  path: string,
  init: RequestInit,
): Promise<Response> => {
  try {
    const response = await fetch(`${SUPABASE_URL}${path}`, {
      ...init,
      headers: {
        apikey: SUPABASE_PUBLISHABLE_KEY,
        ...(init.headers || {}),
      },
    })
    if (response.ok) return response
    throw new SupabaseRequestError(
      (await getErrorMessage(response)) || 'Prijava nije uspjela.',
      response.status,
    )
  } catch (error) {
    if (error instanceof SupabaseRequestError) throw error
    throw new SupabaseRequestError(
      'Nije moguće povezati se sa Supabaseom. Provjerite internetsku vezu.',
      0,
    )
  }
}

export const signInWithPassword = async (
  email: string,
  password: string,
): Promise<SupabaseSession> => {
  const response = await authFetch('/auth/v1/token?grant_type=password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })
  const session = parseSession((await response.json()) as AuthSessionResponse)
  persistSession(session)
  return session
}

export const refreshSession = async (): Promise<SupabaseSession | null> => {
  const session = getStoredSession()
  if (!session) return null

  try {
    const response = await authFetch(
      '/auth/v1/token?grant_type=refresh_token',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: session.refreshToken }),
      },
    )
    const nextSession = parseSession(
      (await response.json()) as AuthSessionResponse,
    )
    persistSession(nextSession)
    return nextSession
  } catch {
    persistSession(null)
    return null
  }
}

export const getActiveSession = async (): Promise<SupabaseSession | null> => {
  const session = getStoredSession()
  if (!session) return null
  const isExpiring = session.expiresAt * 1000 < Date.now() + 60_000
  return isExpiring ? refreshSession() : session
}

export const isAdminSession = (session: SupabaseSession | null): boolean =>
  session?.user.app_metadata?.role === 'admin'

export const requireAdminSession = async (): Promise<SupabaseSession> => {
  const session = await getActiveSession()
  if (!session || !isAdminSession(session)) {
    throw new SupabaseRequestError(
      'Nedostaju administratorska dopuštenja.',
      403,
    )
  }
  return session
}

export const signOut = async (): Promise<void> => {
  const session = getStoredSession()
  persistSession(null)
  if (!session) return
  await fetch(`${SUPABASE_URL}/auth/v1/logout`, {
    method: 'POST',
    headers: {
      apikey: SUPABASE_PUBLISHABLE_KEY,
      Authorization: `Bearer ${session.accessToken}`,
    },
  }).catch(() => undefined)
}

export const encodeStoragePath = (path: string) =>
  path
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/')

export const getPublicStorageUrl = (bucket: string, path: string) =>
  `${SUPABASE_URL}/storage/v1/object/public/${bucket}/${encodeStoragePath(path)}`

export const getSupabaseProjectUrl = () => SUPABASE_URL

export const supabaseFetch = async (
  path: string,
  init: RequestInit = {},
  options: { requireAuth?: boolean } = {},
): Promise<Response> => {
  const session = await getActiveSession()
  if (options.requireAuth && !session) {
    throw new SupabaseRequestError('Administratorska prijava je istekla.', 401)
  }

  let response: Response
  try {
    response = await fetch(`${SUPABASE_URL}${path}`, {
      ...init,
      headers: {
        apikey: SUPABASE_PUBLISHABLE_KEY,
        Authorization: `Bearer ${session?.accessToken || SUPABASE_PUBLISHABLE_KEY}`,
        ...(init.headers || {}),
      },
    })
  } catch {
    throw new SupabaseRequestError(
      'Nije moguće povezati se sa Supabaseom. Provjerite internetsku vezu.',
      0,
    )
  }

  if (response.ok) return response
  const message = await getErrorMessage(response)
  if (response.status === 401) persistSession(null)
  throw new SupabaseRequestError(
    message || `Supabase je vratio grešku (${response.status}).`,
    response.status,
  )
}
